import React from 'react';
import {Button, Drawer, Form, Input} from 'antd';

import {useAddAccessoryMutation, useUpdateAccessoryMutation} from '../../store/api/carsApi';
import {messageType, showMessages} from '../../helpers/message';
import {nothingToUpdate} from '../../helpers/text';

import {Accessory} from '../../types/carTypes';
import {BaseError} from '../../types/apiTypes';

interface AccessoryDrawerProps {
    accessory?: Accessory;
    action: 'Add' | 'Update';
    handleClose: () => void;
    drawerOpen: boolean;
}

const AccessoryDrawer = ({accessory, handleClose, drawerOpen, action}: AccessoryDrawerProps) => {
    const drawerWidth = window.innerWidth <= 400 ? "100%" : "378px";
    const [form] = Form.useForm();

    const [addAccessory, {isLoading: addLoading, isSuccess: addSuccess, isError: isAddError, error: addError, reset: addReset}] = useAddAccessoryMutation();
    const [updateAccessory, {isLoading: updateLoading, isSuccess: updateSuccess, isError: isUpdateError, error: updateError, reset: updateReset}] = useUpdateAccessoryMutation();

    const resetDrawer = React.useCallback(() => {
        form.resetFields();
        handleClose();
    }, [form, handleClose]);

    React.useEffect(() => {
        // ForceRender prop on Drawer is needed to stop a useForm error, which causes an issue with setFieldValue,
        //  needing it to be conditionally called in this useEffect.
        if (drawerOpen && accessory) form.setFieldValue('name', accessory.name);
    }, [accessory, drawerOpen, form]);

    React.useEffect(() => {
        if (updateSuccess || addSuccess) {
            showMessages(`Accessory successfully ${action === 'Add' ? 'added' : 'updated'}.`, messageType.success);
            resetDrawer();
            // Reset the update/add mutations to avoid this useEffect running multiple times.
            updateReset();
            addReset();
        } else if (isUpdateError || isAddError) {
            showMessages(((isAddError ? addError : updateError) as BaseError).data?.message, messageType.error);
            updateReset();
            addReset();
        }
    }, [action, addError, addReset, addSuccess, isAddError, isUpdateError, resetDrawer, updateError, updateReset, updateSuccess]);

    const handleFinish = (values: {name: string, id?: number}) => {
        if (form.isFieldsTouched() && accessory?.name !== values.name) {
            if (action === 'Add') addAccessory({name: values.name});
            if (action === 'Update' && accessory) updateAccessory({id: accessory.id, name: values.name});
        } else {
            showMessages(nothingToUpdate, messageType.error);
        }
    }

    return (
        <Drawer
            width={drawerWidth}
            open={drawerOpen}
            title={`${action === 'Update' ? 'Edit' : 'Add'} Accessory`}
            forceRender={true}
            onClose={resetDrawer}
        >
            <Form
                name="accessory"
                form={form}
                onFinish={handleFinish}
            >
                <Form.Item
                    name="name"
                    label="Name"
                    rules={[{type: 'string', required: true}]}
                >
                    <Input name="name"/>
                </Form.Item>
                <Button
                    type="default"
                    onClick={resetDrawer}
                    className="accessory-drawer-cancel-btn"
                >
                    Cancel
                </Button>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={updateLoading || addLoading}
                >
                    {action}
                </Button>
            </Form>
        </Drawer>
    );
}

export default AccessoryDrawer;
